/**
 * Color Configuration
 *
 * Centralized color constants for consistent theming across the application.
 * This ensures all components use the same color scheme and makes it easy
 * to update colors globally.
 */

export const THEME_COLORS = {
  // Highlight colors for important text
  HIGHLIGHT: "#fbbf24", // amber-400 - primary highlight color
  HIGHLIGHT_SECONDARY: "#f59e0b", // amber-500 - secondary highlight

  // Navigation color for in-article navigation links (match LinkedIn button blue)
  NAVIGATION: "#3b82f6", // blue-500 (lighter)

  // Brand colors
  PRIMARY: "#8b5cf6", // violet-500
  SECONDARY: "#a855f7", // purple-500

  // Text colors
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#d1d5db", // gray-300
  TEXT_MUTED: "#9ca3af", // gray-400

  // Background colors
  BG_GRADIENT_FROM: "#111827", // gray-900
  BG_GRADIENT_VIA: "#581c87", // purple-900
  BG_GRADIENT_TO: "#5b21b6", // violet-900
};

/**
 * Get the primary highlight color for text emphasis
 * @returns {string} The hex color code for highlighting
 */
export const getHighlightColor = () => THEME_COLORS.HIGHLIGHT;

/**
 * Get the navigation link color for navigation sections
 * @returns {string} The hex color code for navigation links
 */
export const getNavigationColor = () => THEME_COLORS.NAVIGATION;

/**
 * Get Innovation Team gradient text classes for consistent styling
 * @returns {string} CSS classes for innovation gradient text
 */
export const getInnovationTextClasses = () =>
  "text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400";

/**
 * Replace any existing color styles with the consistent highlight color
 * @param {string} content - HTML content that may contain inline color styles
 * @returns {string} Content with standardized highlight colors
 */
export const standardizeHighlightColors = (content) => {
  if (!content || typeof content !== "string") return content;

  // Replace color styles BUT exclude navigation blue (#3b82f6)
  return content.replace(
    /color:\s*#(?!3b82f6)([a-fA-F0-9]{6})|color:\s*#(?!3b8)([a-fA-F0-9]{3})|color:\s*red|color:\s*purple|color:\s*violet/g,
    `color: ${THEME_COLORS.HIGHLIGHT}`,
  );
};

export default THEME_COLORS;
